import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import EditIcon from "../../assets/icons/edit.svg";
import ShareIcon from "../../assets/icons/share.svg";
import DeleteIcon from "../../assets/icons/delete.svg";
import CopyIcon from "../../assets/icons/copy.svg";
import CheckIcon from "../../assets/icons/checked.svg";
import OfficeIcon from '../../assets/icons/OfficeList.svg';
import OtherIcon from '../../assets/icons/OtherList.svg';
import HomeIcon from '../../assets/icons/HomeLIst.svg';
import { useLocalization } from "../Context/MultiLanguageContext";
import LocationIcon from '../../assets/icons/Newmap.svg';

import {
  EmailShareButton,
  WhatsappShareButton,
  FacebookShareButton,
  TwitterShareButton,
} from "react-share";
import { EmailIcon, WhatsappIcon, FacebookIcon, TwitterIcon } from "react-share";
import { Address } from "../../API/Address/ViewAllAddedAddressByUserAsync";
import { viewAddedAddressByIdAsync } from "../../API/Address/ViewAddedAddressByIdAsync";
import { requestDeleteAddressAsync } from "../../API/Address/RequestDeleteAddressAsync.ts";
import { addressPrivacy } from "../../API/Address/AddressPrivacy";
import MapAddressDetails from "../Maps/MapAddressDetails";
import Footer from "../Footer/Footer";
import Header from "../Header/Header";
import { getUserProfile, UserProfile } from "../../API/UserProfileApi/GetUserProfile";
import { ShareLink } from "../../API/Address/SharePost";
import { getUserIdFromToken } from "../../auth/authUtils";
import ReactGA from "react-ga4";
import DeleteModal from '../SideBar/DeleteModal';
import { getAddressImageAsync } from "../../API/Address/GetAddressImageAsync";
import { Eye, Image as ImageIcon, X } from "lucide-react";

const AddressDetails: React.FC<{ updateUserName: (newUserName: string) => void }> = ({ updateUserName }) => {
  const [showResults, setShowResults] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { id } = useParams<{ id: string }>();
  const [addresses, setAddresses] = useState<any[]>([]);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [deleteReason, setDeleteReason] = useState("");
  const [isPamCodeFocused, setIsPamCodeFocused] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState("");
  const [isFormChanged, setIsFormChanged] = useState(false);
  const [copiedField, setCopiedField] = useState<{ type: "pamCode" | "digiPin" | "url"; value: string } | null>(null);
  const [shareUrls, setShareUrls] = useState<{ [key: string]: { url: string; referralLink?: string } }>({});
  const [isLoadingShare, setIsLoadingShare] = useState<{ [key: string]: boolean }>({});

  const shareRef = useRef(null);
  const [userName, setUserName] = useState('');
  const location = useLocation();
  const stateAddress = (location.state as { address?: Address })?.address;
  const [address, setAddress] = useState<any | null>(null);

  type AddressImage = {
    id: number;
    url: string;
  };

  const [addressImages, setAddressImages] = useState<AddressImage[]>([]);
  const [selectedImage, setSelectedImage] = useState<AddressImage | null>(null);

  const [isLoadingImages, setIsLoadingImages] = useState(false);

  const navigate = useNavigate();
  const { t } = useLocalization();

  const buildFormattedPamCode = (address: any) => {
    const pin = String(address?.pinCode ?? "").trim();
    const block = String(address?.blockCode ?? "").trim();
    const addr = String(address?.addressCode ?? "").trim();

    if (pin && block && addr) {
      return `${pin}-${block}-${addr}`;
    }

    return formatPamCode(address?.pamCode || "");
  };

  const togglePrivacy = async () => {
    setAddress((prev: any) =>
      prev ? { ...prev, isPrivate: !prev.isPrivate } : prev
    );

    const token = localStorage.getItem("token");
    if (!token) {
      setErrorMessage(t("sessionExpired"));
      navigate("/");
      return;
    }

    try {
      await addressPrivacy(Number(id), token, t);
    } catch (error: any) {
      setErrorMessage(`${t("errorUpdating")}: ${error.message}`);

      setAddress((prev: any) =>
        prev ? { ...prev, isPrivate: !prev.isPrivate } : prev
      );
    } finally {
      await setViewAll();
    }
  };

  const setViewAll = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setErrorMessage(t("sessionExpired"));
      navigate("/");
      return;
    }

    try {
      const addressFromId = await viewAddedAddressByIdAsync(id || "", token, t);

      const addressData = addressFromId?.[0] ?? null;

      setAddress(addressData);

      if (addressData) {
        await loadAddressImages(addressData);
      }
    } catch (error: any) {
      const errorMessage = error.message;
      setErrorMessage(errorMessage);
    }
  };

  useEffect(() => {
    setViewAll();
  }, []);

  useEffect(() => {
    const storedUserName = localStorage.getItem('userName');
    if (storedUserName) {
      setUserName(storedUserName);
    }
  }, []);

  useEffect(() => {
    return () => {
      addressImages.forEach((image) => {
        URL.revokeObjectURL(image.url);
      });
    };
  }, [addressImages]);

  const fetchAndHandleUserProfile = async () => {
    try {
      const data = await getUserProfile(t);
      setUserProfile(data);
      setUserName(data.userName);
      updateUserName(data.userName);
      navigate('/profile', {
        state: {
          userName: data.userName,
          email: data.email,
          phoneNumber: data.phoneNumber,
        },
      });
    } catch (error) {
      toast.error(t("sessionExpired"));
      navigate('/home');
    }
  };

  useEffect(() => {
    if (!address) {
      setErrorMessage(t("noAddressData"));
    }
  }, [address]);

  useEffect(() => {
    setIsFormChanged(deleteReason.trim().length > 0);
  }, [deleteReason]);

  const formatPamCode = (pamCode: string) => {
    if (!pamCode) return "N/A";

    if (pamCode.includes("-")) return pamCode;

    const pincode = pamCode.slice(0, 6);
    const blockCode = pamCode.slice(6, 10);
    const addressCode = pamCode.slice(10);

    if (addressCode) {
      return `${pincode}-${blockCode}-${addressCode}`;
    }

    return `${pincode}-${blockCode}`;
  };

  const loadAddressImages = async (addressData: any) => {
    if (!addressData?.images || addressData.images.length === 0) {
      setAddressImages([]);
      return;
    }

    setIsLoadingImages(true);

    try {
      const token = localStorage.getItem("token") || "";

      const loadedImages: AddressImage[] = [];

      for (const image of addressData.images) {
        try {
          const imageBlob = await getAddressImageAsync(
            Number(addressData.id),
            Number(image.id),
            token,
            t
          );

          if (imageBlob) {
            const imageUrl = URL.createObjectURL(imageBlob);

            loadedImages.push({
              id: Number(image.id),
              url: imageUrl,
            });
          }
        } catch (error) {
          console.error(`Failed to load image ${image.id}:`, error);
        }
      }

      setAddressImages(loadedImages);
    } finally {
      setIsLoadingImages(false);
    }
  };

  const handleEdit = () => {
    if (address) {
      navigate(`/edit-address/${address.id}`, { state: { address } });
    }
  };

  const handleShareClick = async () => {
    if (showShareOptions) {
      setShowShareOptions(false);
      return;
    }

    if (!address?.id) {
      console.error("No address ID available for sharing");
      return;
    }

    setIsLoadingShare(prev => ({ ...prev, [address.id]: true }));

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error(t("sessionExpired"));
        navigate('/');
        return;
      }

      const shareResponse = await ShareLink(parseInt(address.id), token);

      if (shareResponse && shareResponse.url) {
        setShareUrls(prev => ({
          ...prev,
          [address.id]: {
            url: shareResponse.url,
            referralLink: shareResponse.referralLink
          }
        }));
        setShowShareOptions(true);
      } else {
        toast.error(t("errorGeneratingShareLink"));
      }
    } catch (error) {
      console.error('Error calling share API:', error);
      toast.error(t("errorGeneratingShareLink"));
    } finally {
      setIsLoadingShare(prev => ({ ...prev, [address.id]: false }));
    }
  };

  const handleDelete = (addressId: string) => {
    setShowDeleteModal(true);
    setAddressToDelete(addressId);
    setDeleteReason('');
  };

  const handleDeleteSubmit = async () => {
    const id = addressToDelete;
    if (!id) {
      toast.error(t("addressIdMissing"));
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setErrorMessage(t("sessionExpired"));
      navigate('/');
      return;
    }

    try {
      await requestDeleteAddressAsync(parseInt(id), deleteReason, token);

      setAddresses((prevAddresses) =>
        prevAddresses.map((address) =>
          address.id === id ? { ...address, isDeleteRequestSubmitted: true } : address
        )
      );

      setShowDeleteModal(false);
      setDeleteReason('');
      setAddressToDelete('');
      setTimeout(() => {
        setSuccessMessage("");
        navigate("/home");
      }, 3000);
    } catch (error) {
      setErrorMessage(t("deleteRequestfailed"));
    }
  };

  const handleLocationIconClick = (latitude: number, longitude: number) => {
    ReactGA.event({
      category: "Favourites",
      action: "Clicked Location Icon",
      label: `Lat: ${latitude}, Lng: ${longitude}`,
    });
    const defaultMap = localStorage.getItem("defaultMap") || "google";

    if (defaultMap === "waze") {
      const wazeMapsUrl = `https://waze.com/ul?ll=${latitude},${longitude}&navigate=yes`;
      window.open(wazeMapsUrl, '_blank');
    } else if (defaultMap === "mapindia") {
      const mapplsUrl = `https://www.mappls.com/@${latitude},${longitude}`;
      window.open(mapplsUrl, "_blank");
    } else if (defaultMap === "apple") {
      const appleMapsUrl = `https://maps.apple.com/?ll=${latitude},${longitude}&q=${latitude},${longitude}&z=18&t=m`;
      window.open(appleMapsUrl, "_blank");
    } else {
      const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
      window.open(googleMapsUrl, '_blank');
    }
  };

  const handleCopy = async (value: string, type: "pamCode" | "digiPin" | "url") => {
    if (!value) return;
    try {
      const finalValue = String(value ?? "").trim();
      await navigator.clipboard.writeText(finalValue);
      setCopiedField({ type, value: finalValue });
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('userToken');
    localStorage.removeItem('authToken');
    localStorage.removeItem('token');
    localStorage.removeItem('userName');
    sessionStorage.clear();

    navigate("/", { replace: true });
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteReason('');
    setAddressToDelete('');
  };

  const shareMessage = (address: Address) => {
    const shareData = shareUrls[address.id];
    const dynamicUrl = shareData?.url;
    const referralLink = shareData?.referralLink;
    const fallbackUrl = `https://app.getmethere.in/${buildFormattedPamCode(address)}`;

    const addressLink = dynamicUrl || fallbackUrl;

    const isPreprod = process.env.REACT_APP_API_BASE_URL === "https://preprod-api.getmethere.in";
    const firstName = (userName || "").trim().split(/\s+/)[0] || "";
    const userId = getUserIdFromToken();
    const userRefLink = referralLink
      ? referralLink
      : (isPreprod
        ? `https://preprod.getmethere.in/?ref=${userId || ""}&by=${firstName}`
        : `https://getmethere.in/?ref=${userId || ""}&by=${firstName}`);

    return `Check out this address:
    ${address?.addressLine1}, ${address?.addressLine2}, ${address?.addressLine3}
    PAM Code: ${buildFormattedPamCode(address)}
    Digi Pin: ${address?.digiPin}
    Address Link: ${addressLink}

    More details: ${userRefLink}`;
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <ToastContainer />
      <Header userName={userName} fetchUserProfile={fetchAndHandleUserProfile} handleLogout={handleLogout} />

      {stateAddress ? (
        <main className="container mx-auto px-4 py-6">
          <div className="bg-white rounded-md p-4 md:p-6 ">
            <div className="relative h-[250px] sm:h-[300px] md:h-[350px]">
              {address ? (
                <MapAddressDetails addresses={[address]} />
              ) : (
                <p className="text-center text-gray-500">{t("loadingMap")}</p>
              )}
            </div>

            {successMessage && (
              <p className="mt-4 text-center text-green-500">{successMessage}</p>
            )}

            {address ? (
              <div className="mt-2">
                <div className="p-4 bg-white rounded-lg relative space-y-4">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div className="flex gap-2 text-lg font-bold items-center">
                      {['office', 'home', 'other'].includes(address.addressType.toLowerCase()) ? (
                        <>
                          <img
                            src={
                              address.addressType.toLowerCase() === 'office'
                                ? OfficeIcon
                                : address.addressType.toLowerCase() === 'home'
                                  ? HomeIcon
                                  : OtherIcon
                            }
                            alt="Type Icon"
                            className="w-8 h-8"
                          />
                          <span>{t(address.addressType.toLowerCase())}</span>
                        </>
                      ) : (
                        <>
                          <img src={OtherIcon} alt="Custom Type Icon" className="w-8 h-8" />
                          <span>{address.addressType}</span>
                        </>
                      )}
                    </div>

                    <div className="flex items-center space-x-3">
                      <label htmlFor="privacySwitch" className="text-sm font-medium text-gray-700">{t("addressPrivacy")} : </label>

                      <span
                        className="text-sm font-medium text-gray-700 cursor-default"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {address?.isPrivate ? t("private") : t("public")}
                      </span>
                      <input
                        type="checkbox"
                        id="privacySwitch"
                        checked={address?.isPrivate}
                        onChange={togglePrivacy}
                        className="cursor-pointer relative w-11 h-6 rounded-full appearance-none bg-gray-300 checked:bg-blue-600 transition-colors duration-200 outline-none
                      before:content-[''] before:absolute before:w-5 before:h-5 before:rounded-full before:bg-white before:shadow before:top-0.5 before:left-0.5
                      checked:before:translate-x-5 before:transition-transform before:duration-200"
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <p className="text-base">{t("buildingNumber")} : {address.addressLine1}</p>
                    <p className="text-base">{t("street")} : {address.addressLine2}</p>
                    <p className="text-base">{t("city")} : {address.addressLine3}</p>
                    {/* <p>{t("postalCode")} : {address.pinCode}</p> */}
                    <p className="text-base">{t("postOffice")} : {address.postOfficeName}</p>
                  </div>

                  

                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mt-4">
                    <div className="flex items-center gap-2 w-full lg:w-auto">
                      <label htmlFor="pamCode" className="text-base font-medium">
                        {t("pamCode")} :
                      </label>
                      <div className="relative flex-1 lg:w-[200px]">
                        <input
                          id="pamCode"
                          type="text"
                          value={buildFormattedPamCode(address)}
                          className="text-base p-2 pr-10 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                          readOnly
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                        />
                        <button
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-blue-500"
                          aria-label="Copy PAM Code"
                          onClick={() => handleCopy(buildFormattedPamCode(address), "pamCode")}
                        >
                          <img
                            src={copiedField?.type === "pamCode" && copiedField.value === buildFormattedPamCode(address) ? CheckIcon : CopyIcon}
                            alt="Copy"
                            className="w-6 h-6"
                          />
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 w-full lg:w-auto">
                      <label htmlFor="digipin" className="text-base font-medium">
                        {t("digiPin")} :
                      </label>
                      <div className="relative flex-1 lg:w-[200px]">
                        <input
                          id="digipin"
                          type="text"
                          value={address.digiPin}
                          className="text-base p-2 pr-10 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                          readOnly
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                        />
                        <button
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-blue-500"
                          aria-label="Copy DigiPin"
                          onClick={() => handleCopy(address?.digiPin || '', "digiPin")}
                        >
                          <img
                            src={copiedField?.type === "digiPin" && copiedField.value === address?.digiPin ? CheckIcon : CopyIcon}
                            alt="Copy"
                            className="w-6 h-6"
                          />
                        </button>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 justify-end items-center lg:ml-auto mt-4 lg:mt-0">
                      <button
                        onClick={handleShareClick}
                        disabled={isLoadingShare[address?.id]}
                        className={`w-10 h-10 flex items-center justify-center p-1 border border-gray-200 bg-white rounded-md hover:bg-blue-100 focus:outline-none ${isLoadingShare[address?.id] ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {isLoadingShare[address?.id] ? (
                          <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <img src={ShareIcon} alt="Share" className="w-5 h-5" />
                        )}
                      </button>

                      <button
                        title="Location"
                        className="w-10 h-10 flex items-center justify-center p-1 border border-gray-200 bg-white rounded-md hover:bg-blue-100 focus:outline-none"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLocationIconClick(address.latitude, address.longitude);
                        }}
                      >
                        <img
                          src={LocationIcon}
                          alt="Location"
                          className="w-5 h-5 cursor-pointer"
                        />
                      </button>

                      <button
                        onClick={handleEdit}
                        className="w-10 h-10 flex items-center justify-center p-1 border border-gray-200 bg-white rounded-md hover:bg-blue-100 focus:outline-none"
                      >
                        <img src={EditIcon} alt="Edit" className="w-5 h-5" />
                      </button>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (address.isDeleteRequestSubmitted) {
                            if (!toast.isActive('delete-request')) {
                              toast.info(
                                t("deleteRequestSubmitted"),
                                { toastId: 'delete-request' }
                              );
                            }
                          } else {
                            handleDelete(address.id);
                          }
                        }}
                        title="Delete"
                        className="w-10 h-10 flex items-center justify-center p-1 border border-gray-200 bg-white rounded-md hover:bg-blue-100 focus:outline-none"
                      >
                        <img src={DeleteIcon} alt="Delete" className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  {address && addressImages.length > 0 && (
                    <section className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4 sm:p-5" aria-labelledby="address-images-heading">
                      <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <ImageIcon className="h-5 w-5 text-blue-600" aria-hidden="true" />
                            <h3 id="address-images-heading" className="text-base font-semibold text-slate-900">
                              Address images
                            </h3>
                            {!isLoadingImages && (
                              <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700">
                                {addressImages.length}
                              </span>
                            )}
                          </div>
                          <p className="mt-1 text-sm text-slate-500">
                            Review the images attached to this address.
                          </p>
                        </div>
                      </div>

                      {isLoadingImages ? (
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3" aria-label="Loading address images">
                          {[1, 2, 3].map((placeholder) => (
                            <div key={placeholder} className="h-56 animate-pulse rounded-lg bg-slate-200" />
                          ))}
                        </div>
                      ) : (
                        <div className="flex flex-wrap items-center gap-4">
                          {addressImages.map((image, index) => (
                            <div
                              key={image.id}
                              className="group relative w-full max-w-[260px] overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-md"
                            >
                              <button
                                type="button"
                                onClick={() => setSelectedImage(image)}
                                className="block h-[170px] w-full bg-slate-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                                aria-label={`View address image ${index + 1} maximized`}
                              >
                                <img
                                  src={image.url}
                                  alt={`Address image ${index + 1}`}
                                  className="h-full w-full object-cover"
                                />
                              </button>
                              <button
                                type="button"
                                onClick={() => setSelectedImage(image)}
                                className="absolute right-2 top-2 inline-flex h-8 w-8 items-center justify-center rounded-md bg-black/60 text-white hover:bg-black/80 focus:outline-none focus:ring-2 focus:ring-white"
                                title="View image"
                                aria-label={`View address image ${index + 1}`}
                              >
                                <Eye className="h-4 w-4" aria-hidden="true" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </section>
                  )}

                  {(address.url || address.notes) && (
                    <div className="min-w-0 max-w-full space-y-2 overflow-hidden text-md text-gray-700">
                      {address.url && (
                        <p className="flex min-w-0 max-w-full items-start gap-2 break-words">
                          <span className="shrink-0 font-medium text-gray-700">URL:</span>
                          <span className="min-w-0 break-all [overflow-wrap:anywhere] text-blue-600 underline">{address.url}</span>
                        
                        </p>
                      )}
                      {address.notes && (
                        <p className="min-w-0 max-w-full whitespace-pre-wrap [overflow-wrap:anywhere]">
                          <span className="font-medium text-gray-700">Notes:</span>{" "}
                          <span className="text-gray-600">{address.notes}</span>
                        </p>
                      )}
                    </div>
                  )}

                </div>

                

                {showShareOptions && (
                  <div className="flex gap-4 justify-end mr-20">
                    <EmailShareButton
                      url={shareMessage(address)}
                      className="transform transition-transform duration-300 hover:scale-150 hover:shadow-lg"
                    >
                      <EmailIcon
                        size={32}
                        round
                        style={{
                          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                          borderRadius: '50%',
                        }}
                      />
                    </EmailShareButton>
                    <WhatsappShareButton
                      url={shareMessage(address)}
                      className="transform transition-transform duration-300 hover:scale-150 hover:shadow-lg"
                    >
                      <WhatsappIcon
                        size={32}
                        round
                        style={{
                          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                          borderRadius: '50%',
                        }}
                      />
                    </WhatsappShareButton>
                    <FacebookShareButton
                      url={shareMessage(address)}
                      className="transform transition-transform duration-300 hover:scale-150 hover:shadow-lg"
                    >
                      <FacebookIcon
                        size={32}
                        round
                        style={{
                          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                          borderRadius: '50%',
                        }}
                      />
                    </FacebookShareButton>
                    <TwitterShareButton
                      url={shareMessage(address)}
                      className="transform transition-transform duration-300 hover:scale-150 hover:shadow-lg"
                    >
                      <TwitterIcon
                        size={32}
                        round
                        style={{
                          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                          borderRadius: '50%',
                        }}
                      />
                    </TwitterShareButton>
                  </div>
                )}
              </div>
            ) : (
              <p className="mt-6 text-center text-gray-500">{t("loadingAddressDetails")}</p>
            )}
          </div>
        </main>
      ) : (
        <main className="p-4 sm:p-6 flex-grow flex items-center justify-center min-h-[550px]">
          <div className="text-center justify-center">
            <p className="text-gray-500 mb-6">
              {errorMessage || t("sessionExpired")}
            </p>
            <button
              onClick={() => navigate("/addresslist")}
              className="py-2 px-4 bg-blue-600 text-white rounded-md shadow hover:bg-blue-700 focus:outline-none"
            >
              {t("backToAddressList")}
            </button>
          </div>
        </main>
      )}

      <DeleteModal
        isOpen={showDeleteModal}
        addressId={addressToDelete}
        t={t}
        onClose={() => {
          setShowDeleteModal(false);
          setDeleteReason("");
          setAddressToDelete("");
        }}
        navigate={navigate}
        setErrorMessage={setErrorMessage}
        setSuccessMessage={setSuccessMessage}
        setAddresses={setAddresses}
      />

      {selectedImage && (
        <div
          className="fixed inset-0 z-[1100] flex items-center justify-center bg-slate-950/60 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Maximized address image"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-h-full max-w-6xl" onClick={(event) => event.stopPropagation()}>
            <img src={selectedImage.url} alt="Maximized address image" className="max-h-[90vh] max-w-full rounded-lg object-contain shadow-2xl" />
              <button
                type="button"
              onClick={() => setSelectedImage(null)}
              className="absolute right-2 top-2 rounded-full bg-black/60 p-2 text-white hover:bg-black/80"
              aria-label="Close maximized image"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default AddressDetails;